import * as moment from 'moment';
import {
    CustomStorageRegimeStage,
    Evaluation,
    SampleActionTemplate,
    ScheduledAction,
    ScheduledActionTemplate,
    StorageRegime,
    StorageRegimeStage,
} from '@core/data';
import { coerseDateProperty, parseDuration, addDuration } from '@core/utils';

export function isCustomStage(stage: StorageRegimeStage): stage is CustomStorageRegimeStage {
    return (stage as CustomStorageRegimeStage).offsetUnit !== undefined;
}

// Custom stages resolve as a direct harvest-relative offset; Legacy stages resolve exactly as before — their storage durations summed from the start date (no anchoring, no real Legacy regime uses it).
function resolveStageDate(stage: StorageRegimeStage, startDate: moment.Moment | string): moment.Moment {
    if (isCustomStage(stage)) {
        return moment(startDate).add(stage.offsetValue, stage.offsetUnit);
    }

    let result = moment(startDate);
    (stage.storage || []).forEach(store => {
        const dur = parseDuration(store.duration);
        if (dur) result = addDuration(result, dur);
    });
    return result;
}

// Each stage's own display label, falling back to a stand-in so an error message is never blank.
function stageLabel(stage: StorageRegimeStage): string {
    return stage.label || '(untitled stage)';
}

// The real, persisted title shown on checklists/schedules/"Recent Changes" for a generated row — sentence case, so 'move'/'packout' read as 'Move'/'Packout' everywhere this title is displayed. A falsy type (an in-progress Scheduled Action whose type hasn't been picked yet) passes through unchanged rather than throwing.
export function actionTypeLabel(type: string): string {
    return type ? type.charAt(0).toUpperCase() + type.slice(1) : type;
}

// Authoring-time validation of a Custom Regime's stage set — empty, negative offset, duplicate stageKey. No graph/cycle concept remains — stage-to-stage anchoring was removed entirely.
export function validateStorageRegimeStages(stages: CustomStorageRegimeStage[]): string[] {
    const errors: string[] = [];

    if (!stages.length) {
        errors.push('A regime needs at least one stage.');
        return errors;
    }

    const byKey = new Map<string, CustomStorageRegimeStage>();
    const duplicateKeys = new Set<string>();

    stages.forEach(stage => {
        if (!stage.stageKey) return;
        if (byKey.has(stage.stageKey)) duplicateKeys.add(stage.stageKey);
        byKey.set(stage.stageKey, stage);
    });

    duplicateKeys.forEach(key => {
        errors.push(`More than one stage uses the key '${key}'.`);
    });

    stages.forEach(stage => {
        if (stage.offsetValue == null || stage.offsetValue < 0 || !Number.isInteger(stage.offsetValue)) {
            errors.push(`Stage '${stageLabel(stage)}' needs a whole-number harvest offset of zero or more.`);
        }
    });

    return errors;
}

// Client-side mirror of StorageRegimeController::validateStageReferences() — every
// precedesStageKey (a Scheduled Action, or a stage-referencing destroy Sample-level Action)
// must point at a stageKey that actually exists in this regime's own stage set. Checked here so
// the builder can surface it immediately; the server re-checks as defense-in-depth.
export function validateStageReferences(
    stages: CustomStorageRegimeStage[],
    scheduledActions: ScheduledActionTemplate[],
    sampleActions: SampleActionTemplate[],
): string[] {
    const stageKeys = new Set(stages.map(stage => stage.stageKey));

    const referencingEntries = [
        ...scheduledActions,
        ...sampleActions.filter(action => action.precedesStageKey != null),
    ];

    const dangling = new Set(
        referencingEntries
            .map(entry => entry.precedesStageKey)
            .filter(key => key && !stageKeys.has(key)),
    );

    return Array.from(dangling).map(key => `No stage exists for the key '${key}'.`);
}

// Resolves a lead-time entry (a Scheduled Action, or a stage-referencing destroy Sample-level
// Action) against the stage it precedes — "before" subtracts the lead time, isAfter adds it.
function resolveLeadTimeDate(
    stageDate: moment.Moment,
    offsetValue: number,
    offsetUnit: 'days' | 'weeks',
    isAfter: boolean | undefined,
): moment.Moment {
    return isAfter
        ? moment(stageDate).add(offsetValue, offsetUnit)
        : moment(stageDate).subtract(offsetValue, offsetUnit);
}

// Generates the Scheduled/Sample-level Action rows for a Sample from a regime's config — the caller must pass an already-resolved stageKey->evalKey map (evaluationsByStageKey), since a regime's config array order is not guaranteed to match dependency order.
export function scheduledActionsFromStorageRegime(
    sampleKey: string,
    regime: StorageRegime,
    evaluationsByStageKey: Record<string, string>,
    startDate: moment.Moment | string,
): Partial<ScheduledAction>[] {
    const stages = (regime.config && regime.config.evaluations) || [];
    const scheduledActions = (regime.config && regime.config.scheduledActions) || [];
    const sampleActions = (regime.config && regime.config.sampleActions) || [];

    const stageByKey = new Map<string, StorageRegimeStage>(
        stages.filter(isCustomStage).map(stage => [stage.stageKey, stage]),
    );

    // color/overdueThresholdDays are resolved server-side from sourceStorageRegimeKey
    // (applyRegimeSnapshot()) — never set directly here, matching evaluationsFromStorageRegime's
    // own pattern of trusting the server for the snapshot.
    const rowBase = {
        sampleKey,
        sourceStorageRegimeKey: regime.key,
    };

    const stageTiedRows: Partial<ScheduledAction>[] = scheduledActions
        .filter(action => stageByKey.has(action.precedesStageKey))
        .map(action => {
            const stage = stageByKey.get(action.precedesStageKey);
            const stageDate = resolveStageDate(stage, startDate);
            const dueDate = resolveLeadTimeDate(stageDate, action.offsetValue, action.offsetUnit, action.isAfter);

            return {
                ...rowBase,
                evalKey: evaluationsByStageKey[action.precedesStageKey] ?? null,
                dueDate: coerseDateProperty(dueDate),
                title: actionTypeLabel(action.type),
                type: action.type,
                isAfter: !!action.isAfter,
                metadata: action.metadata ?? null,
            };
        });

    // A stage-less 'destroy' produces no row here — scheduled_actions.dueDate is NOT NULL and it has no computable date.
    const sampleLevelRows: Partial<ScheduledAction>[] = sampleActions
        .filter(action => action.type === 'intake' || action.type === 'treatment' || (action.type === 'destroy' && action.precedesStageKey && stageByKey.has(action.precedesStageKey)))
        .map(action => {
            if (action.type === 'intake') {
                return {
                    ...rowBase,
                    evalKey: null,
                    dueDate: coerseDateProperty(moment(startDate)),
                    title: actionTypeLabel(action.type),
                    type: action.type,
                    isAfter: false,
                    metadata: action.metadata ?? null,
                };
            }

            // Relative to harvest, never a stage — mirrors Intake's own day-zero resolution above,
            // just with a non-zero-capable offset instead of a fixed day zero.
            if (action.type === 'treatment') {
                return {
                    ...rowBase,
                    evalKey: null,
                    dueDate: coerseDateProperty(moment(startDate).add(action.offsetValue, action.offsetUnit)),
                    title: actionTypeLabel(action.type),
                    type: action.type,
                    // always after harvest, never before — unlike Destroy/Scheduled Actions, Treatment
                    // has no before/after choice at all, so this is never read from a user toggle.
                    isAfter: true,
                    metadata: action.metadata ?? null,
                };
            }

            const stage = stageByKey.get(action.precedesStageKey);
            const stageDate = resolveStageDate(stage, startDate);
            const dueDate = resolveLeadTimeDate(stageDate, action.offsetValue, action.offsetUnit, action.isAfter);

            return {
                ...rowBase,
                evalKey: null,
                dueDate: coerseDateProperty(dueDate),
                title: actionTypeLabel(action.type),
                type: action.type,
                isAfter: !!action.isAfter,
                metadata: action.metadata ?? null,
            };
        });

    return [...stageTiedRows, ...sampleLevelRows];
}

/**
 * Generates the Evaluations Stencil Behavior produces for a Sample, from a
 * selected Storage Regime — adapted from EvaluationScheduleFormDialog's
 * evaluationsForSample(), driven by StorageRegime.config instead of the
 * static Legacy Library. Every stage is an Evaluation stage now — the
 * type: 'action' stage-template concept was removed entirely — each
 * resolves via resolveStageDate() above.
 */
export function evaluationsFromStorageRegime(
    sampleKey: string,
    regime: StorageRegime,
    protocolId: string,
    startDate: moment.Moment | string,
    size: number,
): Partial<Evaluation>[] {
    const stages = (regime.config && regime.config.evaluations) || [];

    return stages.map(stage => {
        const start = resolveStageDate(stage, startDate);

        return {
            label: stage.label,
            evalStartDate: coerseDateProperty(start),
            evalEndDate: coerseDateProperty(start.clone().add(1, 'days')),
            protocolId,
            scheduleId: regime.isGlobal ? (regime.config?.legacyId ?? null) : null,
            size,
            // Custom stages carry no storage/temperature data anymore (that concept now belongs to Scheduled Actions) — only Legacy stages still populate this.
            storageRegime: isCustomStage(stage) ? [] : (stage.storage || []),
            color: stage.color ?? regime.config?.color ?? null,
            sourceStorageRegimeKey: regime.key,
            sampleKey,
        };
    });
}
